import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus } from 'lucide-react';
import { useTranslation } from '../../i18n';

import { Button } from '../../components/Shared/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/Shared/Tabs';

import { MonthlyRunPanel } from './components/MonthlyRunPanel';
import { StandingRunPanel } from './components/StandingRunPanel';
import { CURRENT_MONTH, CURRENT_YEAR } from './components/billableRun';
import { defaultStandingPlan, type StandingPlan } from '@/utils/standingPlan';
import { formatServiceMonths } from '../../types/billing';

type RunTab = 'monthly' | 'standing';

/**
 * Invoice runs by the object's billing (FAFD-27): „Monatslauf“ (monthly objects and the extra
 * work of standing-invoice objects) and „Dauerrechnungen“ (the fixed amounts of standing-invoice
 * objects, FACF-106).
 */
export default function InvoiceCreatePage() {
  const { t } = useTranslation(['invoices', 'common']);
  const navigate = useNavigate();

  const [tab, setTab] = useState<RunTab>('monthly');
  const [year, setYear] = useState<number>(CURRENT_YEAR);
  const [month, setMonth] = useState<number>(CURRENT_MONTH);
  const [standingPlan, setStandingPlan] = useState<StandingPlan>(() => defaultStandingPlan());

  const period = `${String(month).padStart(2, '0')}/${year}`;
  const subtitle = tab === 'standing'
    ? t('invoices:standing.subtitle', { period: formatServiceMonths(standingPlan.from, standingPlan.to) ?? '—' })
    : t('invoices:billable.subtitle', { period });

  return (
    <div className="space-y-4 sm:space-y-6">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => navigate('/invoices')}
        leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={2} />}
      >
        {t('invoices:actions.back')}
      </Button>

      {/* Header card */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
              {t('invoices:actions.create')}
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">{subtitle}</p>
          </div>
          <Button
            variant="secondary"
            onClick={() => navigate('/invoices/new/manual')}
            leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
            className="w-full sm:w-auto"
          >
            {t('invoices:actions.createManual')}
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as RunTab)}>
        <TabsList>
          <TabsTrigger value="monthly">{t('invoices:createTabs.monthly')}</TabsTrigger>
          <TabsTrigger value="standing">{t('invoices:createTabs.standing')}</TabsTrigger>
        </TabsList>

        <TabsContent value="monthly">
          <MonthlyRunPanel
            year={year}
            month={month}
            onYearChange={setYear}
            onMonthChange={setMonth}
          />
        </TabsContent>

        <TabsContent value="standing">
          <StandingRunPanel plan={standingPlan} onPlanChange={setStandingPlan} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
